"use client";

import { usePathname } from "next/navigation";
import { NavDropdown } from "./nav-dropdown";
import { ZoneLink } from "./zone-link";

export type NavLink = {
	href: string;
	label: string;
	/** One-line "what you'll find here" shown under the label in NavDropdown.
	 *  Optional so flat links and the mobile menu can ignore it. */
	description?: string;
};

export type NavGroup = {
	/** Optional catalog section id (see nav-catalog.ts NavSection) — lets a
	 *  consumer branch per-section (e.g. mobile-nav-menu.tsx collapsing the
	 *  "business" section for guests) without string-matching the label. */
	key?: string;
	label: string;
	items: NavLink[];
};

// A NavEntry is either a flat link or a dropdown group — "items" in entry
// discriminates between the two (see isNavGroup below).
export type NavEntry = NavLink | NavGroup;

type NavLinksProps = {
	items: NavEntry[];
	ariaLabel: string;
};

const baseClass =
	"rounded-xl px-3 py-2 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

function isNavGroup(entry: NavEntry): entry is NavGroup {
	return "items" in entry;
}

export function NavLinks({ items, ariaLabel }: NavLinksProps) {
	const pathname = usePathname();

	return (
		<nav
			aria-label={ariaLabel}
			className="hidden items-center gap-1 whitespace-nowrap text-sm text-muted-foreground sm:flex"
		>
			{items.map((entry) => {
				if (isNavGroup(entry)) {
					return (
						<NavDropdown
							key={entry.label}
							label={entry.label}
							items={entry.items}
						/>
					);
				}
				const isActive =
					pathname === entry.href || pathname.startsWith(`${entry.href}/`);
				return (
					<ZoneLink
						key={entry.href}
						href={entry.href}
						aria-current={isActive ? "page" : undefined}
						className={`${baseClass} ${
							isActive
								? "bg-accent text-foreground"
								: "hover:bg-accent hover:text-foreground"
						}`}
					>
						{entry.label}
					</ZoneLink>
				);
			})}
		</nav>
	);
}
